import React, { useState, useRef, useEffect } from 'react';
import { Plus, ChevronUp, ChevronDown, GripVertical } from 'lucide-react';
import GtmBin from './GtmBin';

/* Q5'S COMPARISON AND Q6'S THREE SECTIONS — Max, 2026-10-07: "question 5 thì cho nó dạng bảng",
   "question 6 thì chia làm 3 section và có thể add line trong mỗi section".

   Both were prose on this screen and both are TABLES in Jihwan's document, which is the whole of
   why Max is asking: a paragraph cannot be added to a line at a time, and the thing people do with
   these two questions is add lines to them.

   THEY LIVE IN THEIR OWN FILE because 41_Gtm.jsx is already the longest screen in the module and
   this is a self-contained pair. They hold NO state but the add row: every cell reports to the
   sheet's own `rowEdits` through `cellOf`/`cellSet`, so one Save at the foot of the page writes
   the prose, the ranked lists and these together — which is the rule Max set the same day
   ("tôi muốn nút edit apply cho toàn bộ phiếu") and the reason neither has an Edit button. */

/* The add row, shared. It is the only state either table holds: a line being typed does not exist
   yet, so there is no row for `rowEdits` to key it by.
   EVERY FIELD AT ONCE — Max, 2026-10-16: "điền các trường cùng 1 lúc chứ ko phải điền stage xong rồi
   điền thêm những cái khác". `fields` are the line's columns, the first being its name (required);
   `cols` is the table's own grid, so each box sits under the column it fills (`null` in `fields` is
   a spacer for a column the add row has nothing for, such as the grip). */
const AddLine = ({ t, label, placeholder, busy, onAdd, extra, fields, cols }) => {
    const list = fields || [{ key: 'label', placeholder }];
    const blank = () => Object.fromEntries(list.filter(Boolean).map((f) => [f.key, '']));
    const [v, setV] = useState(blank);
    const first = list.find(Boolean).key;
    const go = async () => {
        if (!String(v[first] || '').trim()) return;
        const out = Object.fromEntries(Object.entries(v).map(([k, x]) => [k, String(x || '').trim()]));
        await onAdd(out[first], out);
        setV(blank());
    };
    return (
        <div className={`gtm-addline${cols ? ' grid' : ''}`} style={cols ? { gridTemplateColumns: cols } : undefined}>
            {list.map((f, i) => (f ? (
                <input key={f.key} type={f.type || 'text'} list={f.list}
                    value={v[f.key]} onChange={(e) => setV({ ...v, [f.key]: e.target.value })}
                    placeholder={f.placeholder} aria-label={f.placeholder}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); go(); } }}
                />
            ) : <span key={`sp${i}`} />))}
            {extra}
            <button type="button" className="gtm-btn" disabled={busy || !String(v[first] || '').trim()} onClick={go}>
                <Plus size={13} /> {label}
            </button>
        </div>
    );
};

/* ---- Q5 · what the inline chair and the edition each carry ------------------------------------ */
export const GtmSpecTable = ({ t, d, canEdit, busy, cellOf, cellSet, onAdd, onRemove, onOrder }) => {
    /* ORDERED LIKE EVERY OTHER TABLE HERE — Max, 2026-10-08: "cả bảng này". Its order is the
       document's reading order and nothing could change it; the grip, the number and the drag are
       the same grammar the rest of the sheet now speaks. */
    const [rows, setRows] = useState(d.specs || []);
    const [from, setFrom] = useState(null);
    const [over, setOver] = useState(null);
    const dirty = useRef(false);
    useEffect(() => {
        if (from === null && !dirty.current) setRows(d.specs || []);
    }, [d.specs, from]);
    const commit = (next) => {
        setRows(next);
        dirty.current = true;
        Promise.resolve(onOrder(next.map((r) => ({ id: r.spec_pk_id }))))
            .finally(() => { dirty.current = false; });
    };
    const move = (i, by) => {
        const j = i + by;
        if (j < 0 || j >= rows.length) return;
        const next = rows.slice();
        const [r] = next.splice(i, 1);
        next.splice(j, 0, r);
        commit(next);
    };
    const drop = (to) => {
        if (from === null || to === from) { setFrom(null); setOver(null); return; }
        const next = rows.slice();
        const [r] = next.splice(from, 1);
        next.splice(from < to ? to - 1 : to, 0, r);
        setFrom(null); setOver(null);
        commit(next);
    };
    /* ITEM AND CONTENT, TWO COLUMNS — So-yeon Shin, 2026-10-16: "Please remove the Edition field
       and rename Inline to 'Content', allowing users to add items and enter the corresponding
       content." The table compared an inline chair with an edition, which is GTM-26-001's
       question and not every programme's; the per-programme column headings (`spec_a_name` /
       `spec_b_name`) stand down with it, since "Content" is now the column's own name.
       THE EDITION COLUMN STAYS ONLY WHERE IT HOLDS SOMETHING. On GTM-26-001 three items (Initials,
       Reading light, Pocket) say what they are ONLY in the edition column — hiding it would print
       those rows with nothing beside them on an approved document. So a programme whose rows carry
       an edition value keeps the column, headed Edition; every other programme, and every new
       one, has the two. */
    const hasEdition = rows.some((r) => String(r.edition_text || '').trim());
    if (!rows.length && !canEdit) return null;
    return (
        <div className="gtm-sect">
            <h2>{t('gtm.specHead')}</h2>
            <p className="hint">{t('gtm.specHint')}</p>
            <div className="gtm-tablewrap">
                <table className="gtm-table gtm-spec">
                    <colgroup>
                        <col style={{ width: canEdit ? 54 : 34 }} />
                        <col style={{ width: 240 }} /><col />
                        {hasEdition && <col />}
                        {canEdit && <col style={{ width: 96 }} />}
                    </colgroup>
                    <thead>
                        <tr>
                            <th aria-label={t('gtm.orderHead')} />
                            <th>{t('gtm.specItem')}</th>
                            <th>{t('gtm.specContent')}</th>
                            {hasEdition && <th>{t('gtm.specB')}</th>}
                            {canEdit && <th aria-label={t('gtm.remove')} />}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={r.spec_pk_id}
                                className={over === i && from !== null ? 'over' : undefined}
                                draggable={canEdit}
                                onDragStart={() => setFrom(i)}
                                onDragOver={(e) => { if (from !== null) { e.preventDefault(); setOver(i); } }}
                                onDragEnd={() => { setFrom(null); setOver(null); }}
                                onDrop={(e) => { e.preventDefault(); drop(i); }}>
                                <td className="gtm-rgrip">
                                    {/* THE FLEX GOES ON A SPAN, NOT ON THE `<td>`. Setting
                                        `display: flex` on a table cell takes it out of the table
                                        layout model, so the column stopped aligning and the grip
                                        was clipped against the card's edge — seen on Max's own
                                        screenshot, not in the markup. */}
                                    <span className="gtm-rgrip-in">
                                        {canEdit && <GripVertical size={12} aria-hidden="true" />}
                                        <span className="gtm-rankno">{i + 1}</span>
                                    </span>
                                </td>
                                {['item_name', 'inline_text', ...(hasEdition ? ['edition_text'] : [])].map((f) => (
                                    /* NAMED, not found by position. A check read this row's first
                                       `td` for the item name and broke the moment the grip cell
                                       was added in front of it — the same fault twice would be a
                                       habit, so the cell says what it holds. */
                                    <td key={f} className={`gtm-spec-${f}`}>
                                        {canEdit ? (
                                            <input
                                                value={cellOf('spec', r, 'spec_pk_id', f)}
                                                onChange={(e) => cellSet('spec', r.spec_pk_id, f, e.target.value)}
                                            />
                                        ) : (
                                            /* A DASH, NOT AN EMPTY CELL, and the dash is the
                                               SCREEN's — the document prints one where the inline
                                               chair has no such part, and storing the character
                                               would make a row that claims to say something. */
                                            <span className={r[f] ? '' : 'gtm-nil'}>{r[f] || '—'}</span>
                                        )}
                                    </td>
                                ))}
                                {canEdit && (
                                    <td className="gtm-ract">
                                        <span className="gtm-tmove">
                                            <button type="button" disabled={busy || i === 0}
                                                title={t('gtm.moveUp')} aria-label={t('gtm.moveUp')}
                                                onClick={() => move(i, -1)}><ChevronUp size={13} /></button>
                                            <button type="button" disabled={busy || i === rows.length - 1}
                                                title={t('gtm.moveDown')} aria-label={t('gtm.moveDown')}
                                                onClick={() => move(i, 1)}><ChevronDown size={13} /></button>
                                        </span>
                                        <GtmBin t={t} busy={busy} fromDoc={!!r.from_doc}
                                            onRemove={() => onRemove(r.spec_pk_id)} />
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {canEdit && (
                <AddLine t={t} busy={busy} label={t('gtm.addLine')}
                    cols="54px 240px minmax(0, 1fr) auto"
                    fields={[null, { key: 'label', placeholder: t('gtm.specItemPh') },
                        { key: 'inline_text', placeholder: t('gtm.specContent') }]}
                    onAdd={(label, x) => onAdd({ label, inline_text: x.inline_text })} />
            )}
        </div>
    );
};

/* ---- Q6 · three sections, each a list ---------------------------------------------------------- */
/* THE THREE WORDS ARE THE OPEN ITEM'S OWN — pre / launch / post, which is what the launch-stage
   filter at the top of this page already calls them, and the labels come from the same
   `gtm.phase_*` strings. One vocabulary, so the page cannot teach that "Launch" means different
   things in different boxes.
   The item's FOURTH phase, `before`, is deliberately not here: it means "before the work starts",
   which is Q5's question, and Q6 begins at pre-launch. */
const PHASES = ['pre', 'launch', 'post'];

export const GtmDiscover = ({ t, d, canEdit, busy, cellOf, cellSet, onAdd, onRemove }) => {
    const rows = d.discover || [];
    if (!rows.length && !canEdit) return null;
    return (
        <div className="gtm-sect">
            <h2>{t('gtm.discoverHead')}</h2>
            <p className="hint">{t('gtm.discoverHint')}</p>
            {/* THE TWO COLUMNS SAY WHAT THEY ARE — Max, 2026-10-16: "trường bên phải là gì vậy ko thấy
                diễn giải". A heading row, as the timeline has, and each box carries its name too. */}
            <div className="gtm-dline gtm-dhead" aria-hidden="true">
                <span>{t('gtm.discoverItem')}</span><span>{t('gtm.discoverDetail')}</span>
            </div>
            {PHASES.map((ph) => {
                const mine = rows.filter((r) => r.phase === ph);
                return (
                    /* NOT `.gtm-phase` — that class is the small chip on an open item's row, and
                       a section wearing it would be painted as a chip. */
                    <div className={`gtm-dsect ${ph}`} key={ph}>
                        {/* THE SECTION NAMES ITSELF AND COUNTS ITSELF. Three headings with
                            nothing under one of them should read as "nothing here yet" rather
                            than as a heading somebody forgot to delete. */}
                        <h3>{t(`gtm.phase_${ph}`)} <small>{mine.length}</small></h3>
                        {!mine.length && <p className="gtm-nil">{t('gtm.noLines')}</p>}
                        {mine.map((r) => (
                            <div className="gtm-dline" key={r.disc_pk_id}>
                                {canEdit ? (
                                    <>
                                        <input className="gtm-dlab"
                                            placeholder={t('gtm.discoverItem')} aria-label={t('gtm.discoverItem')}
                                            value={cellOf('discover', r, 'disc_pk_id', 'label')}
                                            onChange={(e) => cellSet('discover', r.disc_pk_id, 'label', e.target.value)} />
                                        <input className="gtm-ddet"
                                            placeholder={t('gtm.discoverDetail')} aria-label={t('gtm.discoverDetail')}
                                            value={cellOf('discover', r, 'disc_pk_id', 'detail')}
                                            onChange={(e) => cellSet('discover', r.disc_pk_id, 'detail', e.target.value)} />
                                        {/* NO PHASE BOX ON THE ROW — So-yeon Shin, 2026-10-16: "Since
                                            the stages are already divided into Pre-launch, Launch,
                                            and Post-launch, I think the dropdown on the right can
                                            be removed." A line belongs to the section it was added
                                            in; one that slips is removed and added again below. */}
                                        <span className="gtm-rowdrop">
                                            <GtmBin t={t} busy={busy} fromDoc={!!r.from_doc}
                                                onRemove={() => onRemove(r.disc_pk_id)} />
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <b className="gtm-dlab">{r.label}</b>
                                        <span className="gtm-ddet">{r.detail}</span>
                                    </>
                                )}
                            </div>
                        ))}
                        {canEdit && (
                            <AddLine t={t} busy={busy} label={t('gtm.addLine')}
                                cols="190px minmax(0, 1fr) auto"
                                fields={[{ key: 'label', placeholder: t('gtm.discoverPh') },
                                    { key: 'detail', placeholder: t('gtm.discoverDetail') }]}
                                onAdd={(label, x) => onAdd({ label, phase: ph, detail: x.detail })} />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

/* ---- Slide 16 · Timeline and Owners ------------------------------------------------------------ */
/* THE EXECUTION PLAN, 0 to 7 — Max, 2026-10-08, relaying Jihwan: "Please refer to Page 5 onward,
   starting with the 7 Questions", which is to say the deck does not stop at Q7. This is the one
   part of it the portal had nothing for.

   IT BELONGS TO NO QUESTION. The seven questions are what the programme IS; this is how it gets
   run, so it sits at the foot of the sheet with the programme-wide items rather than being pushed
   under Q7, which would make it read as part of "how do we measure success".

   THE NUMBER IS A FIELD, NOT THE POSITION. Jihwan labels these 0-7 and people say "we're at 3",
   so it is typed and stored — unlike the ranked lists above, where the number IS the row's place
   and a stored one would be a second place for the order to live. */
export const GtmTimeline = ({ t, d, canEdit, busy, cellOf, cellSet, progOf, progSet,
    onAdd, onRemove, onOrder }) => {
    /* THE NUMBER IS THE POSITION — Max, 2026-10-08: "ko để user tự điền số mà số tự điền", in the
       same breath as "cho phép di chuyển các dòng". Those are one decision: a stored number and a
       movable row disagree the first time anybody drags one, and the screen would then print two
       answers to "which step is this". Jihwan's deck starts at 0, so the index does too. */
    /* A LOCAL COPY, FOR THE SAME REASON THE RANKED LISTS KEEP ONE — the row has to follow the hand
       NOW, not after a round trip, or a drag reads as having done nothing. It is replaced by the
       server's answer on every reload EXCEPT mid-drag and mid-save, which would pull the row out
       from under the hand or snap it back to the order the save has just replaced. */
    const [list, setList] = useState(d.timeline || []);
    const [from, setFrom] = useState(null);
    const [over, setOver] = useState(null);
    const dirty = useRef(false);
    useEffect(() => {
        if (from === null && !dirty.current) setList(d.timeline || []);
    }, [d.timeline, from]);
    const rows = list;
    if (!rows.length && !canEdit) return null;

    const commit = (next) => {
        setList(next);
        dirty.current = true;
        Promise.resolve(onOrder(next.map((r) => ({ id: r.tl_pk_id }))))
            .finally(() => { dirty.current = false; });
    };
    const move = (i, by) => {
        const j = i + by;
        if (j < 0 || j >= rows.length) return;
        const next = rows.slice();
        const [row] = next.splice(i, 1);
        next.splice(j, 0, row);
        commit(next);
    };
    /* DRAG — Max, 2026-10-08: "nó thêm tính năng kéo chuột để sắp xếp á". The ↑/↓ stay: a control
       reachable only by dragging is reachable by nobody on a keyboard, and they are what a check
       can press honestly. Dropping ONTO the row you started from is a no-op rather than a save,
       because a drag that goes nowhere is not a reorder. */
    const drop = (to) => {
        if (from === null || to === from) { setFrom(null); setOver(null); return; }
        const next = rows.slice();
        const [row] = next.splice(from, 1);
        next.splice(from < to ? to - 1 : to, 0, row);
        setFrom(null); setOver(null);
        commit(next);
    };
    return (
        <div className="gtm-sect gtm-timeline">
            <h2>{t('gtm.timelineHead')}</h2>
            <p className="hint">{t('gtm.timelineHint')}</p>
            {/* THE HEADING ROW IS DRAWN LOCKED TOO now that there are five columns — an owner's
                name and a date with nothing over them read as two loose facts. */}
            <div className="gtm-tline gtm-thead" aria-hidden="true" draggable={false}>
                <span /><span>{t('gtm.timelineStage')}</span><span>{t('gtm.timelineWho')}</span>
                <span>{t('gtm.timelineWhen')}</span><span>{t('gtm.timelineOwner')}</span>
                {canEdit && <><span /><span /></>}
            </div>
            {rows.map((r, i) => (
                <div className={`gtm-tline${over === i && from !== null ? ' over' : ''}`}
                    key={r.tl_pk_id}
                    draggable={canEdit}
                    onDragStart={() => setFrom(i)}
                    onDragOver={(e) => { if (from !== null) { e.preventDefault(); setOver(i); } }}
                    onDragEnd={() => { setFrom(null); setOver(null); }}
                    onDrop={(e) => { e.preventDefault(); drop(i); }}>
                    {/* READ-ONLY IN BOTH STATES. It was an input for a few hours; a box somebody
                        can type into that then ignores what they typed is worse than a number. */}
                    <b className="gtm-tno">
                        {canEdit && <GripVertical size={12} className="gtm-tgrip" aria-hidden="true" />}
                        {i}
                    </b>
                    {canEdit ? (
                        <>
                            <input className="gtm-tlab"
                                aria-label={t('gtm.timelineStage')} placeholder={t('gtm.timelineStage')}
                                value={cellOf('timeline', r, 'tl_pk_id', 'label')}
                                onChange={(e) => cellSet('timeline', r.tl_pk_id, 'label', e.target.value)} />
                            <input className="gtm-tdet"
                                aria-label={t('gtm.timelineWho')} placeholder={t('gtm.timelineWho')}
                                value={cellOf('timeline', r, 'tl_pk_id', 'detail')}
                                onChange={(e) => cellSet('timeline', r.tl_pk_id, 'detail', e.target.value)} />
                            {/* A DATE, not free text: step 3's own sentence says the launch date is
                                worked back from here, so this column has to be comparable with
                                something. A duration stays in the sentence, where it already is. */}
                            <input className="gtm-twhen" type="date"
                                aria-label={t('gtm.timelineWhen')} title={t('gtm.timelineWhen')}
                                value={String(cellOf('timeline', r, 'tl_pk_id', 'plan_date') || '').slice(0, 10)}
                                onChange={(e) => cellSet('timeline', r.tl_pk_id, 'plan_date', e.target.value)} />
                            {/* THE OWNER, ON THE FAR RIGHT — So-yeon Shin, 2026-10-16: "Please add an
                                Owner field on the far right of the timeline so users can enter the
                                person responsible". The page's one people list (`gtm-people`) offers
                                the names, and a name nobody holds an account for can still be typed:
                                a step is often held by somebody outside the portal. */}
                            <input className="gtm-towner" list="gtm-people"
                                aria-label={t('gtm.timelineOwner')} placeholder={t('gtm.timelineOwner')}
                                value={cellOf('timeline', r, 'tl_pk_id', 'owner_name') || ''}
                                onChange={(e) => cellSet('timeline', r.tl_pk_id, 'owner_name', e.target.value)} />
                            {/* ↑/↓ AND NOT ONLY A DRAG. A control reachable only by dragging is
                                reachable by nobody on a keyboard — the rule the ranked lists keep,
                                and these are what a check can press honestly. Saved immediately:
                                the row moving IS the confirmation, so there is nothing for the
                                sheet's Save to add. */}
                            <span className="gtm-tmove">
                                <button type="button" disabled={busy || i === 0}
                                    title={t('gtm.moveUp')} aria-label={t('gtm.moveUp')}
                                    onClick={() => move(i, -1)}><ChevronUp size={13} /></button>
                                <button type="button" disabled={busy || i === rows.length - 1}
                                    title={t('gtm.moveDown')} aria-label={t('gtm.moveDown')}
                                    onClick={() => move(i, 1)}><ChevronDown size={13} /></button>
                            </span>
                            <span className="gtm-rowdrop">
                                <GtmBin t={t} busy={busy} fromDoc={!!r.from_doc}
                                    onRemove={() => onRemove(r.tl_pk_id)} />
                            </span>
                        </>
                    ) : (
                        <>
                            <b className="gtm-tlab">{r.label}</b>
                            <span className="gtm-tdet">{r.detail}</span>
                            <span className="gtm-twhen">
                                {r.plan_date ? String(r.plan_date).slice(0, 10) : <i className="gtm-nil">—</i>}
                            </span>
                            <span className="gtm-towner">
                                {r.owner_name || <i className="gtm-nil">—</i>}
                            </span>
                        </>
                    )}
                </div>
            ))}
            {canEdit && (
                <AddLine t={t} busy={busy} label={t('gtm.addLine')}
                    cols="42px 190px minmax(0, 1fr) 150px 190px auto"
                    fields={[null, { key: 'label', placeholder: t('gtm.timelineStage') },
                        { key: 'detail', placeholder: t('gtm.timelineWho') },
                        { key: 'plan_date', placeholder: t('gtm.timelineWhen'), type: 'date' },
                        { key: 'owner_name', placeholder: t('gtm.timelineOwner'), list: 'gtm-people' }]}
                    onAdd={(label, x) => onAdd({ label, detail: x.detail, plan_date: x.plan_date, owner_name: x.owner_name })} />
            )}
            {/* WHERE IT GETS SENT. One sentence on the PROGRAMME rather than a ninth row: it is
                about the table, not a step in it — the same call the spec table's two column
                headings took. */}
            {canEdit ? (
                <input className="gtm-tnote" value={progOf('timeline_note')}
                    aria-label={t('gtm.timelineNote')} placeholder={t('gtm.timelineNote')}
                    onChange={(e) => progSet('timeline_note', e.target.value)} />
            ) : (!!progOf('timeline_note') && <p className="gtm-tnote-read">{progOf('timeline_note')}</p>)}
        </div>
    );
};

/* ---- Q7 · what success is measured by ---------------------------------------------------------- */
/* Max, 2026-10-08: "thêm 1 cái bảng để mình điền các đầu mục vào", said in the same breath as
   taking the metrics table off the sheet. The difference between the two is the point: what came
   off read live Shopify sales and froze a baseline against a date — a second feature nobody had
   asked for — and this is the same shape as every other table here, a heading and what it means,
   typed by a person. Q7 is a question like the other six and now answers the same way.

   SEEDED FROM Q7'S OWN PROSE rather than left empty, and the prose trimmed to the one line no
   table carries (sql/20261008f). An empty table beside a paragraph listing the same six headings
   would have been the fifth time this screen printed one fact twice. */
export const GtmMeasure = ({ t, d, canEdit, busy, cellOf, cellSet, onAdd, onRemove, onOrder }) => {
    const [rows, setRows] = useState(d.measures || []);
    const [from, setFrom] = useState(null);
    const [over, setOver] = useState(null);
    const dirty = useRef(false);
    useEffect(() => {
        if (from === null && !dirty.current) setRows(d.measures || []);
    }, [d.measures, from]);
    if (!rows.length && !canEdit) return null;

    const commit = (next) => {
        setRows(next);
        dirty.current = true;
        Promise.resolve(onOrder(next.map((r) => ({ id: r.meas_pk_id }))))
            .finally(() => { dirty.current = false; });
    };
    const move = (i, by) => {
        const j = i + by;
        if (j < 0 || j >= rows.length) return;
        const next = rows.slice();
        const [r] = next.splice(i, 1);
        next.splice(j, 0, r);
        commit(next);
    };
    const drop = (to) => {
        if (from === null || to === from) { setFrom(null); setOver(null); return; }
        const next = rows.slice();
        const [r] = next.splice(from, 1);
        next.splice(from < to ? to - 1 : to, 0, r);
        setFrom(null); setOver(null);
        commit(next);
    };

    return (
        <div className="gtm-sect gtm-measure">
            <h2>{t('gtm.measureHead')}</h2>
            <p className="hint">{t('gtm.measureHint')}</p>
            {canEdit && (
                <div className="gtm-tline gtm-thead" aria-hidden="true" draggable={false}>
                    <span /><span>{t('gtm.measureItem')}</span><span>{t('gtm.measureWhat')}</span>
                    <span /><span /><span />
                </div>
            )}
            {rows.map((r, i) => (
                <div className={`gtm-tline${over === i && from !== null ? ' over' : ''}`}
                    key={r.meas_pk_id}
                    draggable={canEdit}
                    onDragStart={() => setFrom(i)}
                    onDragOver={(e) => { if (from !== null) { e.preventDefault(); setOver(i); } }}
                    onDragEnd={() => { setFrom(null); setOver(null); }}
                    onDrop={(e) => { e.preventDefault(); drop(i); }}>
                    <b className="gtm-tno">
                        {canEdit && <GripVertical size={12} className="gtm-tgrip" aria-hidden="true" />}
                        {i + 1}
                    </b>
                    {canEdit ? (
                        <>
                            <input className="gtm-tlab"
                                aria-label={t('gtm.measureItem')} placeholder={t('gtm.measureItem')}
                                value={cellOf('measure', r, 'meas_pk_id', 'label')}
                                onChange={(e) => cellSet('measure', r.meas_pk_id, 'label', e.target.value)} />
                            <input className="gtm-tdet"
                                aria-label={t('gtm.measureWhat')} placeholder={t('gtm.measureWhat')}
                                value={cellOf('measure', r, 'meas_pk_id', 'detail')}
                                onChange={(e) => cellSet('measure', r.meas_pk_id, 'detail', e.target.value)} />
                            <span />
                            <span className="gtm-tmove">
                                <button type="button" disabled={busy || i === 0}
                                    title={t('gtm.moveUp')} aria-label={t('gtm.moveUp')}
                                    onClick={() => move(i, -1)}><ChevronUp size={13} /></button>
                                <button type="button" disabled={busy || i === rows.length - 1}
                                    title={t('gtm.moveDown')} aria-label={t('gtm.moveDown')}
                                    onClick={() => move(i, 1)}><ChevronDown size={13} /></button>
                            </span>
                            <span className="gtm-rowdrop">
                                <GtmBin t={t} busy={busy} fromDoc={!!r.from_doc}
                                    onRemove={() => onRemove(r.meas_pk_id)} />
                            </span>
                        </>
                    ) : (
                        <>
                            <b className="gtm-tlab">{r.label}</b>
                            <span className="gtm-tdet">{r.detail}</span>
                            <span />
                        </>
                    )}
                </div>
            ))}
            {canEdit && (
                <AddLine t={t} busy={busy} label={t('gtm.addLine')}
                    cols="42px 190px minmax(0, 1fr) auto"
                    fields={[null, { key: 'label', placeholder: t('gtm.measureItem') },
                        { key: 'detail', placeholder: t('gtm.measureWhat') }]}
                    onAdd={(label, x) => onAdd({ label, detail: x.detail })} />
            )}
        </div>
    );
};
